import { WBBox, WBFlex, WBTextFieldV2, WBSelectV2 } from '@admiin-com/ds-web';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getInitials, getNestedError } from '../../../helpers/string';
import { mapCountryCode, COUNTRIES_LIST } from '../../../helpers/entities';
import { MenuItem } from '@mui/material';

export const AddressManualFields = ({
  name,
  isPostal,
}: {
  name: string;
  isPostal?: boolean;
}) => {
  const { t } = useTranslation();
  const inputs = useMemo(
    () => ({
      address1: {
        label: t('address1', { ns: 'onboarding' }),
        name: `${name}.address1` as const,
        type: 'text',
        placeholder: t('address1Placeholder', { ns: 'onboarding' }),
        defaultValue: '',
        rules: {
          required: t('address1Required', { ns: 'onboarding' }),
        },
      },
      address2: {
        label: t('address2', { ns: 'onboarding' }),
        name: `${name}.address2` as const,
        type: 'text',
        placeholder: t('address2Placeholder', { ns: 'onboarding' }),
        defaultValue: '',
        rules: {},
      },
      city: {
        label: t('city', { ns: 'onboarding' }),
        name: `${name}.city` as const,
        type: 'text',
        placeholder: t('cityPlaceholder', { ns: 'onboarding' }),
        defaultValue: '',
        rules: {
          required: t('cityRequired', { ns: 'onboarding' }),
        },
      },
      state: {
        label: t('state', { ns: 'onboarding' }),
        name: `${name}.state` as const,
        type: 'text',
        placeholder: t('statePlaceholder', { ns: 'onboarding' }),
        defaultValue: '',
        rules: {
          required: t('stateRequired', { ns: 'onboarding' }),
        },
      },
      country: {
        label: t('country', { ns: 'onboarding' }),
        name: `${name}.country` as const,
        type: 'text',
        placeholder: t('countryPlaceholder', { ns: 'onboarding' }),
        defaultValue: 'Australia',
        rules: {
          required: t('countryRequired', { ns: 'onboarding' }),
        },
      },
      postcode: {
        label: t('postcode', { ns: 'onboarding' }),
        name: `${name}.postalCode` as const,
        type: 'text',
        placeholder: t('postcodePlaceholder', { ns: 'onboarding' }),
        defaultValue: '',
        rules: {
          required: t('postcodeRequired', { ns: 'onboarding' }),
        },
      },
    }),
    [name, t, isPostal]
  );
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const address1 = watch(inputs.address1.name);
  const address2 = watch(inputs.address2.name);
  const city = watch(inputs.city.name);
  const state = watch(inputs.state.name);
  const country = watch(inputs.country.name);
  const postalCode = watch(inputs.postcode.name);

  useEffect(() => {
    const full = [address1, address2, city, state, postalCode, country]
      .filter(Boolean)
      .join(', ');
    setValue(`${name}.fullAddress`, full);
    setValue(`${name}.stateCode`, getInitials(state ?? ''));
    setValue(`${name}.countryCode`, mapCountryCode(country ?? ''));
  }, [address1, address2, city, state, postalCode, country, name, setValue]);

  return (
    <>
      <WBFlex flexDirection={['column', 'row']}>
        <WBBox flex={3} pr={[0, 3]}>
          <Controller
            control={control}
            name={inputs.address1.name}
            rules={inputs.address1.rules}
            defaultValue={inputs.address1.defaultValue}
            render={({ field }) => (
              <WBTextFieldV2
                {...field}
                label={inputs.address1.label}
                type="text"
                placeholder={inputs.address1.placeholder}
                error={!!getNestedError(errors, field.name)}
                helperText={
                  (getNestedError(errors, field.name)?.message as string) || ''
                }
                margin="dense"
              />
            )}
          />
        </WBBox>
        <WBBox flex={1}>
          <Controller
            control={control}
            name={inputs.address2.name}
            rules={inputs.address2.rules}
            defaultValue={inputs.address2.defaultValue}
            render={({ field }) => (
              <WBTextFieldV2
                {...field}
                label={inputs.address2.label}
                type="text"
                placeholder={inputs.address2.placeholder}
                error={!!getNestedError(errors, field.name)}
                helperText={
                  (getNestedError(errors, field.name)?.message as string) || ''
                }
                margin="dense"
              />
            )}
          />
        </WBBox>
      </WBFlex>

      <WBFlex>
        <Controller
          control={control}
          name={inputs.city.name}
          rules={inputs.city.rules}
          defaultValue={inputs.city.defaultValue}
          render={({ field }) => (
            <WBTextFieldV2
              {...field}
              label={inputs.city.label}
              type="text"
              placeholder={inputs.city.placeholder}
              error={!!getNestedError(errors, field.name)}
              helperText={
                (getNestedError(errors, field.name)?.message as string) || ''
              }
              margin="dense"
            />
          )}
        />
      </WBFlex>
      <WBFlex
        flexDirection={['column', 'row']}
        alignItems={['start', 'end']}
        gap={[0, 3]}
      >
        <Controller
          control={control}
          name={inputs.state.name}
          rules={inputs.state.rules}
          defaultValue={inputs.state.defaultValue}
          render={({ field }) => (
            <WBTextFieldV2
              {...field}
              label={inputs.state.label}
              type="text"
              placeholder={inputs.state.placeholder}
              error={!!getNestedError(errors, field.name)}
              helperText={
                (getNestedError(errors, field.name)?.message as string) || ''
              }
              margin="dense"
            />
          )}
        />

        <Controller
          control={control}
          name={inputs.country.name}
          rules={inputs.country.rules}
          defaultValue={inputs.country.defaultValue}
          render={({ field }) => (
            <WBSelectV2
              {...field}
              fullWidth
              label={inputs.country.label}
              placeholder={inputs.country.placeholder}
              error={!!getNestedError(errors, field.name)}
              helperText={
                (getNestedError(errors, field.name)?.message as string) || ''
              }
              onChange={(e) => field.onChange(e.target.value)}
            >
              {COUNTRIES_LIST.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </WBSelectV2>
          )}
        />
        <Controller
          control={control}
          name={inputs.postcode.name}
          rules={inputs.postcode.rules}
          defaultValue={inputs.postcode.defaultValue}
          render={({ field }) => (
            <WBTextFieldV2
              {...field}
              label={inputs.postcode.label}
              type="text"
              placeholder={inputs.postcode.placeholder}
              error={!!getNestedError(errors, field.name)}
              helperText={
                (getNestedError(errors, field.name)?.message as string) || ''
              }
              margin="dense"
            />
          )}
        />
      </WBFlex>
    </>
  );
};
